import { type ReactNode, Fragment } from 'react';
import { Text } from '@mantine/core';
import { semanticColors } from '../config/theme';
import type { PlaceholderInfo } from '../types';
import { PlaceholderBadge } from '../components/common/PlaceholderBadge';
import { GreetingTemplatePlaceholder } from '../constants/greetingTemplatePlaceholders';
import { humanizePlaceholderToken } from './humanizePlaceholderToken';

/**
 * User-specific values used to replace `{{...}}` tokens in templates for previews
 * and editors. Aligns with backend `TemplateContext` (recipient and sender).
 */
export interface TemplateDisplayContext {
  /** Replaces `{{RECIPIENT_NAME}}` — first + last, trimmed. */
  recipientName?: string;
  /** Replaces `{{RECIPIENT_FIRST_NAME}}`. */
  recipientFirstName?: string;
  /** Replaces `{{SENDER_NAME}}` — the sender / account display name. */
  senderName?: string;
}

/** Regex for `{{NAME}}` tokens: name segment is alphanumerics/underscore, case-insensitive. */
export const TEMPLATE_PLACEHOLDER_TOKEN_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;

/**
 * Builds a display "full name" for placeholder resolution, matching the backend
 * convention (non-empty parts joined with a space).
 */
export function buildRecipientDisplayName(firstName?: string, lastName?: string): string {
  return [firstName, lastName]
    .map((s) => s?.trim())
    .filter((s): s is string => Boolean(s && s.length > 0))
    .join(' ');
}

/**
 * Values for `resolveKnownPlaceholders` from the signed-in user and optional contact.
 * Used when editors open with template or saved text that may still contain `{{...}}` tokens.
 */
export function buildTemplateDisplayContextForEditor(
  user: {
    name?: string | null;
    givenName?: string | null;
    surname?: string | null;
  } | null
  | undefined,
  contact: { firstName: string; lastName: string } | null | undefined,
): TemplateDisplayContext {
  const senderName =
    user?.name?.trim() ||
    buildRecipientDisplayName(user?.givenName ?? undefined, user?.surname ?? undefined) ||
    undefined;
  if (!contact) {
    return { senderName };
  }
  return {
    recipientName: buildRecipientDisplayName(contact.firstName, contact.lastName) || undefined,
    recipientFirstName: contact.firstName?.trim() || undefined,
    senderName,
  };
}

/**
 * Resolves resolvable `{{...}}` tokens in `text` using `ctx`. Unknown or empty
 * tokens are left as-is (still `{{NAME}}` in the string) so callers can
 * re-parse or show badges in rich views.
 */
export function resolveKnownPlaceholders(text: string, ctx: TemplateDisplayContext): string {
  if (!text) {
    return text;
  }
  return text.replace(TEMPLATE_PLACEHOLDER_TOKEN_RE, (match, rawName: string) => {
    const name = rawName.toUpperCase();
    const resolved = getKnownPlaceholderString(name, ctx);
    if (resolved !== undefined && resolved !== '') {
      return resolved;
    }
    return match;
  });
}

/**
 * Returns the string value for a known placeholder, or `undefined` if the name
 * is not supported or the context has no value.
 */
export function getKnownPlaceholderString(
  nameUpper: string,
  ctx: TemplateDisplayContext,
): string | undefined {
  const v = (s?: string): string | undefined => {
    const t = s?.trim();
    return t && t.length > 0 ? t : undefined;
  };
  switch (nameUpper) {
    case GreetingTemplatePlaceholder.RECIPIENT_NAME:
      return v(ctx.recipientName);
    case GreetingTemplatePlaceholder.RECIPIENT_FIRST_NAME:
      return v(ctx.recipientFirstName);
    case GreetingTemplatePlaceholder.SENDER_NAME:
      return v(ctx.senderName);
    default:
      return undefined;
  }
}

export interface RenderTemplateOptions {
  /** Values that replace or annotate placeholders. */
  ctx: TemplateDisplayContext;
  /**
   * Registry from `getPlaceholders()` (descriptions, examples) for tooltips and
   * friendly empty-state labels.
   */
  registry: readonly PlaceholderInfo[];
  /**
   * Optional per-token sample strings (e.g. landing page). Shown in italic when
   * a known placeholder has no `ctx` value.
   */
  sampleValues?: Record<string, string>;
}

/**
 * Renders template text with plain segments, resolved values, sample italics
 * for known empty placeholders, registry-backed badges, or a humanized badge
 * for unknown tokens.
 */
export function renderTemplate(
  value: string,
  { ctx, registry, sampleValues }: RenderTemplateOptions,
): ReactNode {
  if (!value) {
    return null;
  }

  const re = new RegExp(TEMPLATE_PLACEHOLDER_TOKEN_RE);
  re.lastIndex = 0;
  const nodes: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  while ((m = re.exec(value)) !== null) {
    if (m.index > last) {
      nodes.push(<Fragment key={key++}>{value.slice(last, m.index)}</Fragment>);
    }
    const raw = m[1] ?? '';
    const name = raw.toUpperCase();
    const fullToken = m[0];
    const knownValue = getKnownPlaceholderString(name, ctx);

    if (knownValue !== undefined && knownValue !== '') {
      nodes.push(<Fragment key={key++}>{knownValue}</Fragment>);
    } else {
      const sample = sampleValues?.[name];
      if (sample && sample.length > 0) {
        nodes.push(
          <Text
            key={key++}
            component="span"
            c={semanticColors.text.secondary}
            style={{ display: 'inline', fontStyle: 'italic' }}
          >
            {sample}
          </Text>,
        );
      } else {
        const reg = registry.find((p) => p.name.toUpperCase() === name);
        if (reg) {
          nodes.push(
            <PlaceholderBadge
              key={key++}
              name={name}
              title={reg.description}
              example={reg.example}
            />,
          );
        } else {
          const human = humanizePlaceholderToken(name);
          nodes.push(
            <PlaceholderBadge
              key={key++}
              name={name}
              title={fullToken}
              example={undefined}
            >
              {human}
            </PlaceholderBadge>,
          );
        }
      }
    }
    last = m.index + m[0].length;
  }
  if (last < value.length) {
    nodes.push(<Fragment key={key++}>{value.slice(last)}</Fragment>);
  }
  return <>{nodes}</>;
}
